/**
 * Alert — inline notification banner.
 *
 * Design system mapping (mch-alert):
 *   • Layout: grid icon | content | actions
 *   • Padding: px-4 py-3 rounded-md text-sm
 *   • Variants:
 *       default → bg-webex-gray-100  text-webex-gray-1000  / dark: bg-webex-gray-800 text-white
 *       info    → bg-webex-blue-100  text-webex-blue-700   / dark: bg-webex-blue-900 text-webex-blue-400
 *       success → bg-webex-green-100 text-webex-green-700  / dark: bg-webex-green-900 text-webex-green-400
 *       warning → bg-webex-yellow-100 text-webex-yellow-700 / dark: bg-webex-yellow-900 text-webex-yellow-400
 *       error   → bg-webex-red-100   text-webex-red-700    / dark: bg-webex-red-900   text-webex-red-400
 *
 * Accessibility: error/warning use role="alert" (assertive); info/success use role="status" (polite).
 *
 * Compound:
 *   <Alert variant="success">
 *     <Alert.Icon />
 *     <Alert.Content>
 *       <Alert.Title>Saved.</Alert.Title>
 *       <Alert.Description>Your changes are live.</Alert.Description>
 *     </Alert.Content>
 *     <Alert.Actions>
 *       <Button variant="text" size="compact">Undo</Button>
 *     </Alert.Actions>
 *     <Alert.Close />
 *   </Alert>
 */
import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@lib/cn"

export const alertVariants = cva(
  "grid grid-cols-[20px_1fr_auto] items-center gap-2.5 rounded-md px-4 py-3 text-sm",
  {
    variants: {
      variant: {
        default: "bg-webex-gray-100 text-webex-gray-1000 dark:bg-webex-gray-800 dark:text-white",
        info:    "bg-webex-blue-100 text-webex-blue-700 dark:bg-webex-blue-900 dark:text-webex-blue-400",
        success: "bg-webex-green-100 text-webex-green-700 dark:bg-webex-green-900 dark:text-webex-green-400",
        warning: "bg-webex-yellow-100 text-webex-yellow-700 dark:bg-webex-yellow-900 dark:text-webex-yellow-400",
        error:   "bg-webex-red-100 text-webex-red-700 dark:bg-webex-red-900 dark:text-webex-red-400",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
)

const AlertContext = React.createContext<{ variant: VariantProps<typeof alertVariants>["variant"] }>({
  variant: "default",
})

export interface AlertProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof alertVariants> {}

const Root = React.forwardRef<HTMLDivElement, AlertProps>(
  ({ className, variant = "default", role, children, ...props }, ref) => {
    const ariaRole =
      role ?? (variant === "error" || variant === "warning" ? "alert" : "status")
    return (
      <AlertContext.Provider value={{ variant }}>
        <div
          ref={ref}
          role={ariaRole}
          className={cn(alertVariants({ variant }), className)}
          {...props}
        >
          {children}
        </div>
      </AlertContext.Provider>
    )
  },
)
Root.displayName = "Alert"

function Icon({ className, children }: { className?: string; children?: React.ReactNode }) {
  return (
    <span
      aria-hidden="true"
      className={cn("inline-flex size-4 items-center justify-center [&>svg]:size-4 [&>img]:size-4", className)}
    >
      {children ?? <span className="size-3 rounded-full bg-current" />}
    </span>
  )
}

function Content({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("min-w-0", className)} {...props} />
}

function Title({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("font-medium inline mr-1", className)} {...props} />
}

function Description({ className, ...props }: React.HTMLAttributes<HTMLSpanElement>) {
  return <span className={cn("", className)} {...props} />
}

function Actions({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("inline-flex items-center gap-1", className)} {...props} />
}

const Close = React.forwardRef<HTMLButtonElement, React.ButtonHTMLAttributes<HTMLButtonElement>>(
  ({ className, ...props }, ref) => (
    <button
      ref={ref}
      type="button"
      aria-label="Dismiss"
      className={cn(
        "inline-flex size-6 items-center justify-center rounded text-current opacity-70 hover:opacity-100",
        "hover:bg-current/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-current/40",
        className,
      )}
      {...props}
    >
      <svg viewBox="0 0 12 12" className="size-3" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" aria-hidden>
        <path d="M3 3l6 6M9 3l-6 6" />
      </svg>
    </button>
  ),
)
Close.displayName = "Alert.Close"

export const Alert = Object.assign(Root, {
  Icon,
  Content,
  Title,
  Description,
  Actions,
  Close,
})
